import { InterstitialOutro } from '@/api/generated-catch-backend/model';
import BackArrow from '@/app/components/questionnaireV2/icons/back-arrow';
import { RESOURCE_RISK_GRID, RESOURCE_SECTION_DOTS } from '@/app/components/questionnaireV2/utils/lottie-queries';
import type { LottieRefCurrentProps } from 'lottie-react';
import { motion, useAnimationControls, Variant } from 'motion/react';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { NextButton } from '../next-button/button';
import { GraphicLottie } from './interstitial-shared';

const containerVariants: Record<string, Variant> = {
  entering: {
    clipPath: 'inset(0 100% 0 0)',
    transition: { duration: 6 },
  },
  revealed: {
    clipPath: 'inset(0 0 0 0)',
    transition: { duration: 2 },
  },
};

const buttonVariants: Record<string, Variant> = {
  hidden: {
    opacity: 0,
    y: 20,
    transition: { duration: 0.3 },
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

const textVariants: Record<string, Variant> = {
  hidden: {
    opacity: 0,
    y: 20,
    transition: { duration: 0.3 },
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

/**
 * Sliding door animation component
 * 1. Lottie reveals from left to right using clip-path
 * 2. Original Lottie slides to the left, second Lottie appears on the right
 * 3. Dots appear in center, move to bottom, and animate
 * 4. Text appears after dots complete
 */
export const SlidingDoor = ({ onNext, interstitial, onBack, isBackDisabled }: { onNext: () => void; interstitial: InterstitialOutro; onBack?: () => void; isBackDisabled?: boolean }) => {
  const leftControls = useAnimationControls();
  const rightControls = useAnimationControls();
  const dotsControls = useAnimationControls();
  const buttonControls = useAnimationControls();
  const textControls = useAnimationControls();
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const dotsLottieRef = React.useRef<LottieRefCurrentProps>(null);
  const [optimalDotsPosition, setOptimalDotsPosition] = useState<string>('12%');
  const measurementRef = React.useRef<HTMLDivElement>(null);

  const getOptimalCopyCount = () => {
    const lottieWidth = 500;
    const viewportWidth = window.innerWidth;
    const extraPadding = lottieWidth;
    const totalWidthNeeded = viewportWidth + extraPadding * 2;
    const copiesNeeded = Math.ceil(totalWidthNeeded / lottieWidth);
    return Math.max(copiesNeeded, 3);
  };

  // Create dynamic dots variants based on measured optimal position
  const dotsVariants: Record<string, Variant> = React.useMemo(
    () => ({
      hidden: {
        opacity: 0,
        scale: 0.8,
        y: 0, // Start in center
        transition: { duration: 0.3 },
      },
      center: {
        opacity: 1,
        scale: 1,
        y: 0, // Stay in center
        transition: { duration: 0.5, ease: 'easeOut' },
      },
      bottom: {
        opacity: 1,
        scale: 1,
        y: optimalDotsPosition, // Use measured optimal position
        transition: { duration: 0.8, ease: 'easeInOut' },
      },
    }),
    [optimalDotsPosition],
  );

  // Create variants that will be computed at render time
  const isDesktop = window.innerWidth >= 1024;

  const leftLottieVariants: Record<string, Variant> = {
    entering: {
      x: 0,
      y: '-50%',
      opacity: 1,
      clipPath: 'inset(0 100% 0 0)',
      transition: { duration: 6 },
    },
    together: {
      x: 0,
      y: '-50%',
      opacity: 1,
      clipPath: 'inset(0 0 0 0)',
      transition: { duration: 2 },
    },
    split: {
      x: isDesktop ? '-25%' : '-40%',
      y: '-50%',
      opacity: 1,
      clipPath: isDesktop ? 'inset(0 66% 0 0)' : 'inset(0 50% 0 0)',
      transition: { duration: 1, ease: 'easeInOut' },
    },
  };

  const rightLottieVariants: Record<string, Variant> = {
    entering: {
      x: '100%',
      y: '-50%',
      opacity: 0,
      transition: { duration: 6 },
    },
    together: {
      x: '100%',
      y: '-50%',
      opacity: 0,
      transition: { duration: 3 },
    },
    split: {
      x: isDesktop ? '25%' : '40%',
      y: '-50%',
      opacity: 1,
      clipPath: isDesktop ? 'inset(0 0 0 66%)' : 'inset(0 0 0 50%)',
      transition: { duration: 1, ease: 'easeInOut' },
    },
  };

  const slidingDoorFrame = interstitial.frames.find(
    (frame) => frame.graphic?.type === 'predefined' && frame.graphic?.predefinedAnimation === 'sliding-door',
  );

  if (!slidingDoorFrame?.slidingDoorSequence || slidingDoorFrame.slidingDoorSequence.length === 0) {
    throw new Error(`No sliding door sequence configured for interstitial ${interstitial.slug}`);
  }

  const slidingDoorSequence = slidingDoorFrame.slidingDoorSequence;

  const startRevealAnimation = async () => {
    await Promise.all([leftControls.start('together'), rightControls.start('together')]);
    handleRevealComplete();
  };

  // Measure all text sequences on mount to find optimal dots position
  React.useEffect(() => {
    const measureTexts = () => {
      if (!measurementRef.current) return;

      const viewportHeight = window.innerHeight;
      const padding = 48; // Safety padding between text and dots

      // Create a temporary container to measure all texts
      const tempContainer = document.createElement('div');
      tempContainer.className = 'absolute opacity-0 pointer-events-none inset-0 flex items-center justify-center p-8';
      tempContainer.style.zIndex = '-1';
      measurementRef.current.appendChild(tempContainer);

      let maxTextHeight = 0;

      // Measure each text sequence
      slidingDoorSequence.forEach((item) => {
        const textEl = document.createElement('p');
        textEl.className = 'max-w-md rounded-lg p-6 text-center text-lg font-medium text-white';
        textEl.textContent = item.text.replace(/\\n/g, '\n');
        tempContainer.appendChild(textEl);
        const height = textEl.getBoundingClientRect().height;
        maxTextHeight = Math.max(maxTextHeight, height);
        tempContainer.removeChild(textEl);
      });

      // Remove temporary container
      measurementRef.current.removeChild(tempContainer);

      // Calculate optimal position for dots BELOW text
      // Start from center, then add half the text height plus padding
      const requiredSpaceForText = maxTextHeight / 2 + padding;

      // Convert to percentage of viewport
      const percentageOffset = (requiredSpaceForText / viewportHeight) * 100;

      // Clamp between reasonable bounds (8% to 30%)
      const clampedOffset = Math.min(30, Math.max(8, percentageOffset));

      setOptimalDotsPosition(`${clampedOffset}%`);
    };

    // Delay measurement slightly to ensure fonts are loaded
    const timer = setTimeout(measureTexts, 100);

    startRevealAnimation();

    return () => clearTimeout(timer);
  }, []);

  const handleNext = () => {
    if (currentTextIndex < slidingDoorSequence.length - 1) {
      setCurrentTextIndex(currentTextIndex + 1);
    } else {
      onNext();
    }
  };

  const handleRevealComplete = async () => {
    await Promise.all([leftControls.start('split'), rightControls.start('split')]);

    await dotsControls.start('center');
    await dotsControls.start('bottom');
    await new Promise((resolve) => setTimeout(resolve, 200));

    if (dotsLottieRef.current) {
      dotsLottieRef.current.play();
    }

    await new Promise((resolve) => setTimeout(resolve, 1200));
    await Promise.all([textControls.start('visible'), buttonControls.start('visible')]);
  };

  return (
    <div className="relative flex h-full w-full flex-col" ref={measurementRef}>
      {/* Container for the Lottie halves */}
      <motion.div
        className="relative w-full flex-1 origin-center"
        variants={containerVariants}
        initial="entering"
        animate="revealed"
        onAnimationComplete={handleRevealComplete}
      >
        {/* Portal graphics to document.body to escape container constraints */}
        {createPortal(
          <>
            {/* Left Lottie - slides to the left */}
            <motion.div
              className="fixed"
              style={{
                willChange: 'transform',
                zIndex: 50,
                left: 0,
                right: 0,
                top: '40%',
                height: '500px',
                width: '100vw',
              }}
              variants={leftLottieVariants}
              initial="entering"
              animate={leftControls}
            >
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  overflow: 'hidden',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {/* Create pattern of graphics to fill viewport width */}
                {Array.from({ length: getOptimalCopyCount() }, (_, index) => (
                  <div
                    key={index}
                    style={{
                      flexShrink: 0,
                      width: '500px',
                      height: '100%',
                    }}
                  >
                    <GraphicLottie loop srcUrl={RESOURCE_RISK_GRID} className="h-full w-full" />
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Right Lottie - appears on the right */}
            <motion.div
              className="fixed"
              style={{
                willChange: 'transform',
                zIndex: 50,
                left: 0,
                right: 0,
                top: '40%',
                height: '500px',
                width: '100vw',
              }}
              variants={rightLottieVariants}
              initial="entering"
              animate={rightControls}
            >
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  overflow: 'hidden',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {/* Create pattern of graphics to fill viewport width */}
                {Array.from({ length: getOptimalCopyCount() }, (_, index) => (
                  <div
                    key={index}
                    style={{
                      flexShrink: 0,
                      width: '500px',
                      height: '100%',
                    }}
                  >
                    <GraphicLottie loop srcUrl={RESOURCE_RISK_GRID} className="h-full w-full" />
                  </div>
                ))}
              </div>
            </motion.div>
          </>,
          document.body,
        )}

        {/* Section dots animation */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center"
          style={{
            willChange: 'transform',
            zIndex: 60,
          }}
          variants={dotsVariants}
          initial="hidden"
          animate={dotsControls}
        >
          <div className="h-6 w-64">
            <GraphicLottie
              ref={dotsLottieRef}
              autoPlay={false}
              loop={false}
              srcUrl={RESOURCE_SECTION_DOTS}
              className="h-full w-full"
            />
          </div>
        </motion.div>

        {/* Text content centered between graphics */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center p-8"
          style={{
            zIndex: 70,
          }}
          variants={textVariants}
          initial="hidden"
          animate={textControls}
        >
          <p className="max-w-md rounded-lg p-6 text-center text-lg font-medium text-white whitespace-pre-line">
            {slidingDoorSequence[currentTextIndex].text.replace(/\\n/g, '\n')}
          </p>
        </motion.div>
      </motion.div>

      {/* Action button */}
      <motion.div
        className="flex w-full max-w-lg mx-auto items-center gap-3 px-8 pb-safe-offset-8 pt-8"
        style={{
          zIndex: 80,
          position: 'relative',
        }}
        variants={buttonVariants}
        initial="hidden"
        animate={buttonControls}
      >
        {onBack && (
          <button
            onClick={onBack}
            disabled={isBackDisabled}
            className="inline-flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white/60 transition-colors hover:bg-white/20 disabled:opacity-40"
            style={{
              cursor: isBackDisabled ? 'not-allowed' : 'pointer',
              pointerEvents: isBackDisabled ? 'none' : 'auto',
            }}
          >
            <BackArrow />
          </button>
        )}
        <div className="min-w-0 flex-1 [&_button]:w-full [&_button]:rounded-full">
          <NextButton
            text={slidingDoorSequence[currentTextIndex].ctaText}
            onNext={handleNext}
            variant="primary"
          />
        </div>
      </motion.div>
    </div>
  );
};
